<script setup>
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select';
import { computed, ref } from 'vue';
import { Palette, Plus } from 'lucide-vue-next';
import { cn } from '@/lib/utils';
import Tooltip from '@/components/Tooltip2.vue';
import ThemeCreate from './ThemeCreate.vue';
import { $t } from '@/i18n';

const props = defineProps({
  themes: Array,
  class: {
    type: String,
    default: '',
  },
  open: Boolean,
});

const emit = defineEmits(['update:open']);

const isOpen = computed({
  get: () => props.open,
  set: (v) => emit('update:open', v),
});

const modelValue = ref('');

const showCustomThemeDialog = ref(false);
const customThemes = ref(JSON.parse(localStorage.getItem('custom-themes') || '[]'));
const tempThemes = ref([]);

// 默认配色主题
const defaultThemes = [
  {
    value: 'default',
    label: 'themeSelector.default',
    description: 'themeSelector.defaultDescription',
    colors: ['#000000', '#FFFFFF', '#666666', '#F5F5F5'],
    primaryColor: '#000000',
    secondaryColor: '#FFFFFF',
  },
  {
    value: 'black',
    label: 'themeSelector.classicBlackWhite',
    description: 'themeSelector.classicBlackWhiteDescription',
    colors: ['#000000', '#FFFFFF', '#666666', '#F5F5F5'],
    primaryColor: '#000000',
    secondaryColor: '#FFFFFF',
  },
  {
    value: 'ocean',
    label: 'themeSelector.oceanBlue',
    description: 'themeSelector.oceanBlueDescription',
    colors: ['#0EA5E9', '#0284C7', '#0369A1', '#E0F2FE'],
    primaryColor: '#0EA5E9',
    secondaryColor: '#E0F2FE',
  },
  {
    value: 'sunset',
    label: 'themeSelector.sunsetOrange',
    description: 'themeSelector.sunsetOrangeDescription',
    colors: ['#F97316', '#EA580C', '#DC2626', '#FEF3C7'],
    primaryColor: '#F97316',
    secondaryColor: '#FEF3C7',
  },
  {
    value: 'forest',
    label: 'themeSelector.forestGreen',
    description: 'themeSelector.forestGreenDescription',
    colors: ['#059669', '#047857', '#065F46', '#ECFDF5'],
    primaryColor: '#059669',
    secondaryColor: '#ECFDF5',
  },
  {
    value: 'lavender',
    label: 'themeSelector.lavenderPurple',
    description: 'themeSelector.lavenderPurpleDescription',
    colors: ['#8B5CF6', '#7C3AED', '#6D28D9', '#F3E8FF'],
    primaryColor: '#8B5CF6',
    secondaryColor: '#F3E8FF',
  },
  {
    value: 'rose',
    label: 'themeSelector.rosePink',
    description: 'themeSelector.rosePinkDescription',
    colors: ['#EC4899', '#DB2777', '#BE185D', '#FDF2F8'],
    primaryColor: '#EC4899',
    secondaryColor: '#FDF2F8',
  },
  {
    value: 'golden',
    label: 'themeSelector.goldenLuxury',
    description: 'themeSelector.goldenLuxuryDescription',
    colors: ['#F59E0B', '#D97706', '#B45309', '#FFFBEB'],
    primaryColor: '#F59E0B',
    secondaryColor: '#FFFBEB',
  },
  {
    value: 'midnight',
    label: 'themeSelector.midnightBlue',
    description: 'themeSelector.midnightBlueDescription',
    colors: ['#1E293B', '#0F172A', '#020617', '#F1F5F9'],
    primaryColor: '#1E293B',
    secondaryColor: '#F1F5F9',
  },
  {
    value: 'emerald',
    label: 'themeSelector.emeraldLuxury',
    description: 'themeSelector.emeraldLuxuryDescription',
    colors: ['#10B981', '#059669', '#047857', '#D1FAE5'],
    primaryColor: '#10B981',
    secondaryColor: '#D1FAE5',
  },
  {
    value: 'coral',
    label: 'themeSelector.coralWarm',
    description: 'themeSelector.coralWarmDescription',
    colors: ['#FF6B6B', '#FF5252', '#E53E3E', '#FFEBEE'],
    primaryColor: '#FF6B6B',
    secondaryColor: '#FFEBEE',
  },
  {
    value: 'indigo',
    label: 'themeSelector.indigoDeep',
    description: 'themeSelector.indigoDeepDescription',
    colors: ['#6366F1', '#4F46E5', '#3730A3', '#EEF2FF'],
    primaryColor: '#6366F1',
    secondaryColor: '#EEF2FF',
  },
  {
    value: 'amber',
    label: 'themeSelector.amberWarm',
    description: 'themeSelector.amberWarmDescription',
    colors: ['#F59E0B', '#F39C12', '#E67E22', '#FEF3C7'],
    primaryColor: '#F59E0B',
    secondaryColor: '#FEF3C7',
  },
  {
    value: 'teal',
    label: 'themeSelector.tealFresh',
    description: 'themeSelector.tealFreshDescription',
    colors: ['#14B8A6', '#0D9488', '#0F766E', '#F0FDFA'],
    primaryColor: '#14B8A6',
    secondaryColor: '#F0FDFA',
  },
  {
    value: 'burgundy',
    label: 'themeSelector.burgundyElegant',
    description: 'themeSelector.burgundyElegantDescription',
    colors: ['#9F1239', '#881337', '#701A29', '#FDF2F8'],
    primaryColor: '#9F1239',
    secondaryColor: '#FDF2F8',
  },
  {
    value: 'slate',
    label: 'themeSelector.slateModern',
    description: 'themeSelector.slateModernDescription',
    colors: ['#475569', '#334155', '#1E293B', '#F8FAFC'],
    primaryColor: '#475569',
    secondaryColor: '#F8FAFC',
  },
  {
    value: 'copper',
    label: 'themeSelector.copperVintage',
    description: 'themeSelector.copperVintageDescription',
    colors: ['#B45309', '#A16207', '#92400E', '#FFFBEB'],
    primaryColor: '#B45309',
    secondaryColor: '#FFFBEB',
  },
  {
    value: 'turquoise',
    label: 'themeSelector.turquoiseTropical',
    description: 'themeSelector.turquoiseTropicalDescription',
    colors: ['#06B6D4', '#0891B2', '#0E7490', '#ECFEFF'],
    primaryColor: '#06B6D4',
    secondaryColor: '#ECFEFF',
  },
  {
    value: 'plum',
    label: 'themeSelector.plumRich',
    description: 'themeSelector.plumRichDescription',
    colors: ['#A855F7', '#9333EA', '#7C2D92', '#FAF5FF'],
    primaryColor: '#A855F7',
    secondaryColor: '#FAF5FF',
  },
  {
    value: 'sage',
    label: 'themeSelector.sageCalm',
    description: 'themeSelector.sageCalmDescription',
    colors: ['#84CC16', '#65A30D', '#4D7C0F', '#F7FEE7'],
    primaryColor: '#84CC16',
    secondaryColor: '#F7FEE7',
  },
  {
    value: 'charcoal',
    label: 'themeSelector.charcoalSophisticated',
    description: 'themeSelector.charcoalSophisticatedDescription',
    colors: ['#374151', '#1F2937', '#111827', '#F9FAFB'],
    primaryColor: '#374151',
    secondaryColor: '#F9FAFB',
  },
  {
    value: 'crimson',
    label: 'themeSelector.crimsonBold',
    description: 'themeSelector.crimsonBoldDescription',
    colors: ['#DC2626', '#B91C1C', '#991B1B', '#FEF2F2'],
    primaryColor: '#DC2626',
    secondaryColor: '#FEF2F2',
  },
  {
    value: 'navy',
    label: 'themeSelector.navyProfessional',
    description: 'themeSelector.navyProfessionalDescription',
    colors: ['#1E40AF', '#1D4ED8', '#2563EB', '#EFF6FF'],
    primaryColor: '#1E40AF',
    secondaryColor: '#EFF6FF',
  },
  {
    value: 'mint',
    label: 'themeSelector.mintFresh',
    description: 'themeSelector.mintFreshDescription',
    colors: ['#34D399', '#10B981', '#059669', '#ECFDF5'],
    primaryColor: '#34D399',
    secondaryColor: '#ECFDF5',
  },
  {
    value: 'bronze',
    label: 'themeSelector.bronzeClassic',
    description: 'themeSelector.bronzeClassicDescription',
    colors: ['#CD7F32', '#A0522D', '#8B4513', '#FDF6E3'],
    primaryColor: '#CD7F32',
    secondaryColor: '#FDF6E3',
  },
  {
    value: 'steel',
    label: 'themeSelector.steelIndustrial',
    description: 'themeSelector.steelIndustrialDescription',
    colors: ['#71717A', '#52525B', '#3F3F46', '#FAFAFA'],
    primaryColor: '#71717A',
    secondaryColor: '#FAFAFA',
  },
  {
    value: 'ivory',
    label: 'themeSelector.ivoryElegant',
    description: 'themeSelector.ivoryElegantDescription',
    colors: ['#FFF8DC', '#F5F5DC', '#E6E6DC', '#2C2C2C'],
    primaryColor: '#2C2C2C',
    secondaryColor: '#FFF8DC',
  },
  {
    value: 'mahogany',
    label: 'themeSelector.mahoganyRich',
    description: 'themeSelector.mahoganyRichDescription',
    colors: ['#C04A37', '#A0392C', '#7F2D1F', '#FDF2F0'],
    primaryColor: '#C04A37',
    secondaryColor: '#FDF2F0',
  },
];

defineExpose({
  getTheme,
  setTheme,
  resetTheme,
});

function setTheme(theme) {
  if (theme) {
    tempThemes.value = [theme];
    modelValue.value = theme.value;
  } else {
    modelValue.value = '';
    tempThemes.value = [];
  }
}

function getTheme() {
  const list = themeOptions.value;
  const item = list.find((item) => item.value === modelValue.value);
  if (!item) return '';
  if (modelValue.value === 'default') return '';
  return `
@SeedeTheme(${JSON.stringify(item)})
`;
}

function resetTheme() {
  modelValue.value = '';
}

const themeOptions = computed(() => {
  if (props.themes) return props.themes;
  return [...defaultThemes, ...customThemes.value, ...tempThemes.value];
});

const selectedValue = computed(() => {
  const list = themeOptions.value;
  if (!modelValue.value) return list[0];
  return list.find((theme) => theme.value === modelValue.value) || list[0];
});

// 打开自定义主题对话框
function openCustomThemeDialog() {
  showCustomThemeDialog.value = true;
}

// 保存自定义主题
function saveCustomTheme(newTheme) {
  customThemes.value.push(newTheme);
  localStorage.setItem('custom-themes', JSON.stringify(customThemes.value));
  modelValue.value = newTheme.value;
  showCustomThemeDialog.value = false;
}
</script>
<template>
  <div class="flex w-full space-x-2">
    <Tooltip :text="$t('themeSelector.tooltip')">
      <Select v-model="modelValue" v-model:open="isOpen">
        <SelectTrigger
          :class="
            cn(
              'group transition-shadow duration-300 gap-1 px-1 pl-1.5 hover:bg-black/5 hover:border-black/20 data-[state=open]:bg-black/5 data-[state=open]:border-black/20',
              props.class,
            )
          "
        >
          <div class="shrink-0 size-6 flex items-center justify-center">
            <Palette class="size-3.5 text-black/60" />
          </div>
          <div
            class="hidden md:flex items-center flex-1"
            v-if="modelValue && modelValue !== 'default'"
          >
            <div class="flex items-center">
              <div
                v-for="(color, index) in selectedValue?.colors?.slice(0, 3)"
                :key="color"
                class="w-3 h-3 rounded-full border border-white"
                :style="{
                  backgroundColor: color,
                  marginLeft: index > 0 ? '-4px' : '0',
                  zIndex: selectedValue.colors.length - index,
                }"
              ></div>
            </div>
          </div>
        </SelectTrigger>
        <SelectContent
          class="w-[480px] h-[400px] p-1 rounded-[20px] overflow-hidden backdrop-blur-[60px] hide-scroll-buttons flex flex-col"
          style="
            background-color: rgba(255, 255, 255, 0.8);
            border: 1px solid rgba(0, 0, 0, 0.07);
            box-shadow:
              0 20px 25px -5px rgba(0, 0, 0, 0.1),
              0 8px 10px -6px rgba(0, 0, 0, 0.1);
          "
        >
          <div class="flex-1 overflow-y-auto custom-scrollbar min-h-0">
            <SelectGroup>
              <div class="grid grid-cols-2 gap-3">
                <SelectItem
                  v-for="theme in themeOptions"
                  :key="theme.value"
                  :value="theme.value"
                  class="relative transition-all duration-200 p-2 cursor-pointer rounded-xl border border-black/5 focus:bg-black/5 hover:bg-black/5 hover:border-black/10"
                  :class="modelValue === theme.value ? 'bg-black/5 border-black/10' : ''"
                >
                  <div class="flex flex-col gap-2">
                    <div class="flex items-center gap-1.5">
                      <div
                        v-for="color in theme.colors"
                        :key="color"
                        class="w-8 h-8 rounded-full border-2 border-white shadow-sm"
                        :style="{ backgroundColor: color }"
                      ></div>
                    </div>
                    <div class="flex items-center justify-between">
                      <span
                        class="text-sm font-normal"
                        :class="modelValue === theme.value ? 'text-black/90' : 'text-black/50'"
                      >
                        {{ theme.isCustom ? theme.label : $t(theme.label) }}
                      </span>
                    </div>
                  </div>
                </SelectItem>
              </div>
            </SelectGroup>
          </div>

          <!-- 添加自定义主题按钮 - 固定在底部 -->
          <div
            class="shrink-0 mt-3 rounded-xl transition-all duration-200 p-3 cursor-pointer border border-black/10 hover:border-black/20 hover:bg-black/5"
            @click="openCustomThemeDialog"
          >
            <div class="flex items-center justify-center gap-2">
              <Plus class="w-4 h-4 text-black/40" />
              <span class="text-sm font-medium text-black/60">
                {{ $t('themeSelector.addCustomTheme') }}
              </span>
            </div>
          </div>
        </SelectContent>
      </Select>
    </Tooltip>

    <!-- 自定义主题创建组件 -->
    <ThemeCreate v-model:open="showCustomThemeDialog" @theme-created="saveCustomTheme" />
  </div>
</template>

<style>
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* 隐藏滚动箭头按钮 */
.hide-scroll-buttons [data-radix-select-scroll-up-button],
.hide-scroll-buttons [data-radix-select-scroll-down-button] {
  display: none !important;
}
</style>
